import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Article } from "@/lib/types";
import { LeadStory } from "./LeadStory";
import { homeTrack } from "./shared";

const AUTO_MS = 8000;
const IDLE_MS = 12000;

export const LeadCarousel = ({ slides }: { slides: Article[] }) => {
  const n = slides.length;
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState<Set<number>>(() => new Set(n > 1 ? [0, 1] : [0]));
  const [status, setStatus] = useState("");
  const idxRef = useRef(0);
  const idleUntil = useRef(0);
  const hover = useRef(false);
  const focus = useRef(false);
  const touch = useRef<{ x: number; y: number } | null>(null);

  const go = (target: number, source: string) => {
    if (n < 2) return;
    const next = ((target % n) + n) % n;
    idxRef.current = next;
    setIdx(next);
    setLoaded((s) => new Set(s).add(next).add((next + 1) % n));
    if (source !== "auto") {
      idleUntil.current = Date.now() + IDLE_MS;
      setStatus(`Slide ${next + 1} of ${n}: ${slides[next].title}`);
    }
    homeTrack("lead_carousel", `${source}_${next + 1}_${slides[next].slug}`);
  };
  const goRef = useRef(go);
  goRef.current = go;

  useEffect(() => {
    if (n > 1) homeTrack("lead_carousel", `impression_${n}`);
  }, [n]);

  useEffect(() => {
    if (n < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(hover: none)").matches) return;
    const t = setInterval(() => {
      if (hover.current || focus.current || document.hidden) return;
      if (Date.now() < idleUntil.current) return;
      goRef.current(idxRef.current + 1, "auto");
    }, AUTO_MS);
    return () => clearInterval(t);
  }, [n]);

  useEffect(() => {
    if (n < 2) return;
    const t = setTimeout(() => homeTrack("lead_carousel", `view_${idx + 1}_${slides[idx]?.slug}`), 2000);
    return () => clearTimeout(t);
  }, [idx, n, slides]);

  if (!n) return null;
  if (n === 1) return <LeadStory article={slides[0]} top={!slides[0].is_breaking} />;

  return (
    <section role="region" aria-roledescription="carousel" aria-label="Breaking news"
      data-testid="lead-carousel" className="relative h-full"
      onMouseEnter={() => { hover.current = true; }}
      onMouseLeave={() => { hover.current = false; }}
      onFocusCapture={() => { focus.current = true; }}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) focus.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); go(idx + 1, "key"); }
        if (e.key === "ArrowLeft") { e.preventDefault(); go(idx - 1, "key"); }
      }}
      onTouchStart={(e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
      onTouchEnd={(e) => {
        const t0 = touch.current;
        touch.current = null;
        if (!t0) return;
        const dx = e.changedTouches[0].clientX - t0.x;
        const dy = e.changedTouches[0].clientY - t0.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) go(idx + (dx < 0 ? 1 : -1), "swipe");
      }}>
      <div className="grid h-full">
        {slides.map((a, i) => (
          <div key={a.id} aria-hidden={i !== idx} style={{ gridArea: "1 / 1" }}
            data-testid={`lead-slide-${i}`}
            className={`min-w-0 transition-[opacity,visibility,transform] duration-300 motion-reduce:transition-none ${
              i === idx ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-[6px]"}`}>
            <LeadStory article={a} top={!a.is_breaking} eager={i === 0} deferImage={!loaded.has(i)} />
          </div>
        ))}
      </div>
      <div className="absolute bottom-4 right-4 z-20 flex items-center gap-3">
        <span data-testid="carousel-counter"
          className="hidden sm:block font-mono text-[9px] tracking-[0.2em] text-[#7a8299]">
          {String(idx + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
        </span>
        <div className="flex items-center gap-1.5" data-testid="carousel-indicators">
          {slides.map((_, i) => (
            <button key={i} type="button" onClick={() => go(i, "indicator")}
              aria-label={`Go to slide ${i + 1} of ${n}`} data-testid={`carousel-indicator-${i}`}
              className={`h-[3px] rounded-full transition-all duration-300 ${
                i === idx ? "w-6 bg-gradient-to-r from-[#7C3AED] to-[#00E5FF]"
                  : "w-3 bg-white/20 hover:bg-white/40"}`} />
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => go(idx - 1, "prev")} aria-label="Previous story"
            data-testid="carousel-prev"
            className="h-7 w-7 rounded-full border border-[#1c212e] bg-black/40 backdrop-blur flex items-center justify-center text-[#7a8299] hover:text-white hover:border-[#0dd6eb]/60 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={() => go(idx + 1, "next")} aria-label="Next story"
            data-testid="carousel-next"
            className="h-7 w-7 rounded-full border border-[#1c212e] bg-black/40 backdrop-blur flex items-center justify-center text-[#7a8299] hover:text-white hover:border-[#0dd6eb]/60 transition-colors">
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <p aria-live="polite" className="sr-only">{status}</p>
    </section>
  );
};
